import { UpgradeSubscriptionModal } from '@components'

import { Button, ButtonDropdown, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { ModalId } from '../../../../enums/modalId'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useSegment } from '../../../../hooks'
import { ZervantLocale } from '../../../invoices/enums/zervantLocale'
import { DownloadDocumentFileType } from '../../enums/downloadDocumentFileType'
import { InvoiceBillingStage } from '../../enums/invoiceBillingStage'
import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { InvoiceStatus } from '../../enums/invoiceStatus'
import { QuoteStatus } from '../../enums/quoteStatus'
import { SendAsEmailModalVariant } from '../../enums/sendAsEmailModalVariant'
import { useSendAsEInvoiceAction } from '../../hooks/sendAsEInvoice/useSendAsEInvoiceAction'
import { useSendAsEmail } from '../../hooks/sendAsEmail/useSendAsEmail'
import { useDownloadInvoice } from '../../hooks/useDownloadInvoice'
import type { InvoiceCustomer } from '../../types/invoices/invoiceCustomer'
import { MissingEInvoiceDetails } from '../../types/missingEInvoiceDetails'
import { SendAsEmailForm } from '../../types/sendAsEmailForm'
import { getTrackingDocumentType } from '../../utils/getTrackingDocumentType'
import { isDocumentEligibleForXRechnung } from '../../utils/isDocumentEligibleForXRechnung'
import { SendAsEmailModal } from '../SendAsEmailModal'
import { SendEInvoiceRequirementsModal } from '../SendEInvoiceRequirementsModal'
import { SendInvoiceAsEInvoiceModal } from '../SendInvoiceAsEInvoiceModal'
import { getShareDocumentQueryKey } from './utils/getShareDocumentQueryKey'

interface ShareDocumentMenuProps {
  billingStage?: InvoiceBillingStage
  contactId?: string
  customer?: InvoiceCustomer
  documentId: string
  documentLocale?: ZervantLocale
  documentNumber?: string | null
  documentStatus?: InvoiceStatus | QuoteStatus
  documentType: InvoiceDocumentType
  eInvoiceAddressingLine?: string
  missingEInvoiceDetails?: MissingEInvoiceDetails
}

export const ShareDocumentMenu = ({
  billingStage,
  contactId,
  customer,
  documentId,
  documentLocale,
  documentNumber,
  documentStatus,
  documentType,
  eInvoiceAddressingLine,
  missingEInvoiceDetails,
}: ShareDocumentMenuProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const queryClient = useQueryClient()
  const { download, isDownloading } = useDownloadInvoice()
  const { open: openSendAsEmailModal, close: closeSendAsEmailModal } = useModal(ModalId.SendAsEmailModal)

  const { address: customerAddress, customerType } = customer || {}

  const trackingDocumentType = getTrackingDocumentType({ billingStage, documentType })

  const shouldDisplayDownloadAsXRechnungOption = isDocumentEligibleForXRechnung(
    documentType,
    customerAddress?.country,
    customerType,
  )
  const dropdownSize = shouldDisplayDownloadAsXRechnungOption ? 'xl' : 'l'

  const refreshDocument = useCallback(() => {
    const queryKey = getShareDocumentQueryKey({
      id: documentId,
      type: documentType,
    })
    queryClient.invalidateQueries(queryKey)
  }, [documentId, documentType, queryClient])

  const handleSendAsEmailSuccess = useCallback(() => {
    refreshDocument()
    closeSendAsEmailModal()
  }, [refreshDocument, closeSendAsEmailModal])

  // The current hook name (useSendAsEmail) is kept intentionally to avoid widespread changes in props/types for now.
  const { sendAsEmail, isProcessing: isProcessingSendAsEmail } = useSendAsEmail(SendAsEmailModalVariant.Invoice, {
    onSuccess: handleSendAsEmailSuccess,
  })

  const {
    handleSendAsEInvoice,
    handleSendAsEInvoiceModalSubmit,
    isLoadingEligibility,
    isLoadingSubscriptionDetails,
    isProcessingSendingAsEInvoice,
    sendAsEInvoiceModalId,
    sendEInvoiceRequirementsModalId,
    shouldDisplaySendAsEInvoiceOption,
    upgradeSubscriptionModalId,
  } = useSendAsEInvoiceAction({
    customer,
    documentId,
    documentStatus,
    documentType,
    eInvoiceAddressingLine,
    missingEInvoiceDetails,
    onSuccess: refreshDocument,
    trackingEventName: 'XXX - Invoicing Core - Single Document View - Share - Send as e-invoice clicked',
  })

  const isLoading =
    isDownloading || isProcessingSendingAsEInvoice || isLoadingSubscriptionDetails || isLoadingEligibility

  const sendAsEInvoiceDropdownItem = useMemo(
    (): NavItem<string> => ({
      id: 'sendAsEInvoice',
      children: t('external_invoices.editor.side_actions.send_as_e_invoice'),
      iconLeft: 'fileCode',
      value: 'sendAsEInvoice',
    }),
    [t],
  )

  const downloadAsXRechnungDropdownItem = useMemo(
    (): NavItem<string> => ({
      id: 'downloadAsXRechnung',
      children: t('external_invoices.view.download_as_xrechnung'),
      iconLeft: 'download',
      value: 'downloadAsXRechnung',
    }),
    [t],
  )

  const dropdownItems: NavItem<string>[] = [
    {
      children: t('external_invoices.view.send_as_email'),
      iconLeft: 'mail',
      id: 'sendAsEmail',
      value: 'sendAsEmail',
    },
    ...(shouldDisplaySendAsEInvoiceOption ? [sendAsEInvoiceDropdownItem] : []),
    ...(shouldDisplayDownloadAsXRechnungOption ? [downloadAsXRechnungDropdownItem] : []),
    {
      children: t('external_invoices.view.download_as_pdf'),
      iconLeft: 'file',
      id: 'downloadAsPdf',
      value: 'downloadAsPdf',
    },
  ]

  const handleDownloadAsPdf = useCallback(() => {
    track('XXX - Invoicing Core - Single Document View - Share - Download PDF clicked', {
      document: trackingDocumentType,
      documentStatus,
    })

    download({
      downloadDocumentFileType: DownloadDocumentFileType.PDF,
      documentLocale,
      documentType,
      invoiceId: documentId,
      invoiceNumber: documentNumber,
      onSuccess: refreshDocument,
    })
  }, [
    documentStatus,
    documentLocale,
    documentType,
    documentId,
    documentNumber,
    download,
    refreshDocument,
    track,
    trackingDocumentType,
  ])

  const handleDownloadAsXRechnung = useCallback(() => {
    track('XXX - Invoicing Core - Single Document View - Share - Download as XRechnung clicked')
    download({
      downloadDocumentFileType: DownloadDocumentFileType.XML,
      documentLocale,
      documentType,
      invoiceId: documentId,
      invoiceNumber: documentNumber,
      onSuccess: refreshDocument,
    })
  }, [documentId, documentLocale, documentNumber, documentType, download, refreshDocument, track])

  const handleSendAsEmailFormSubmit = (formData: SendAsEmailForm) => {
    sendAsEmail({
      documentType,
      formData,
      invoiceId: Number(documentId),
    })
  }

  const handleActionSelect = useCallback(
    (_id: string, action: string) => {
      switch (action) {
        case 'downloadAsPdf':
          handleDownloadAsPdf()
          break
        case 'downloadAsXRechnung':
          handleDownloadAsXRechnung()
          break
        case 'sendAsEmail':
          track('XXX - Invoicing Core - Single Document View - Share - Send by email clicked', {
            document: trackingDocumentType,
            documentStatus,
          })
          openSendAsEmailModal()
          break
        case 'sendAsEInvoice':
          handleSendAsEInvoice()
          break
      }
    },
    [
      documentStatus,
      handleDownloadAsPdf,
      handleDownloadAsXRechnung,
      handleSendAsEInvoice,
      openSendAsEmailModal,
      track,
      trackingDocumentType,
    ],
  )

  return (
    <>
      <ButtonDropdown
        dropdownSize={dropdownSize}
        items={dropdownItems}
        onSelect={handleActionSelect}
        triggerElement={
          <Button disabled={isLoading} icon="upload" loading={isLoading} variant="outlined">
            {t('external_invoices.view.share_invoice')}
          </Button>
        }
      />
      <SendAsEmailModal
        contactId={contactId}
        documentLocale={documentLocale}
        documentType={documentType}
        id={ModalId.SendAsEmailModal}
        invoiceNumber={documentNumber ?? ''}
        isSubmitting={isProcessingSendAsEmail}
        onSubmit={handleSendAsEmailFormSubmit}
        variant={SendAsEmailModalVariant.Invoice}
        view="invoiceView"
      />
      <SendEInvoiceRequirementsModal
        cancelInvoiceModalId={ModalId.InvoiceCancellationModal}
        id={sendEInvoiceRequirementsModalId}
        missingEInvoiceDetails={missingEInvoiceDetails}
      />
      <SendInvoiceAsEInvoiceModal
        customer={customer}
        id={sendAsEInvoiceModalId}
        missingEInvoiceDetails={missingEInvoiceDetails}
        onSubmit={handleSendAsEInvoiceModalSubmit}
        view="invoiceView"
      />
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        shouldRedirectToZervantPlanPage
        trackingContext={TrackingContext.ShineEInvoicing}
      />
    </>
  )
}
